import { Button } from '@app/app/_components/Button/Button';
import { Message } from '@app/app/_components/Message/Message';
import { useHomeNavigation } from '@app/app/_hooks/useHomeNavigation';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { DropdownArrowIcon } from '@app/app/_icons/DropdownArrowIcon';
import { LogoutIcon } from '@app/app/_icons/LogoutIcon';
import { UserIcon } from '@app/app/_icons/UserIcon';
import { AccessibilityMenu } from '@app/app/_widgets/AccessabilityMenu/AccessibilityMenu';
import { ClientSwitcher } from '@app/app/_widgets/ProfileMenu/components/ClientSwitcher';
import { ProfileMenuButton } from '@app/app/_widgets/ProfileMenu/components/ProfileMenuButton';
import { Client } from '@app/domain/model/Client';
import { Profile } from '@app/domain/model/Profile';
import { useClickOutside } from '@app/lib/click-outside/useClickOutside';
import { logout } from '@app/service/auth/actions/logout';
import { ROUTES } from '@app/infrastructure/routes/routes';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { KeyboardEvent, useCallback, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { EmailIcon } from '@app/app/_icons/EmailIcon';

interface Props {
    profile?: Profile | null;
    clients: Client[];
    selectedClientId?: string;
}

export function ProfileMenu({ profile, clients, selectedClientId }: Props) {
    const t = useTranslations();
    const router = useRouter();
    const { isEsales } = useHomeNavigation();

    const menuRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);

    const handleToggle = useCallback(() => {
        setOpen(isOpen => !isOpen);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            setOpen(false);

            menuRef.current?.focus();
        }
    }, []);

    const handleLogout = useCallback(async () => {
        await logout();

        router.refresh();
    }, [router]);

    useClickOutside(handleClose, [menuRef, popupRef]);

    return (
        <div className="relative max-md:-mr-6">
            <button
                ref={menuRef}
                className={twJoin(
                    'flex cursor-pointer items-center gap-2 text-sm font-medium',
                    open && 'text-primary-500',
                )}
                onClick={handleToggle}
                type="button"
                tabIndex={0}
                aria-label={profile?.shortName ?? t('auth.profile')}
                aria-haspopup={true}
                aria-controls="profile-menu">
                <div className="inline-flex items-center gap-2">
                    <div className={twJoin('rounded-full p-2.5', open ? 'bg-primary-200' : 'bg-neutral-100')}>
                        <UserIcon className={twJoin('size-5', open ? 'text-primary-500' : 'text-neutral-500')} />
                    </div>
                    <span className="hidden md:inline">{profile?.shortName ?? t('auth.profile')}</span>
                </div>
                <DropdownArrowIcon className={twJoin('hidden size-3.5 md:block', open && 'rotate-180')} />
            </button>

            {open && (
                <>
                    <div
                        ref={popupRef}
                        className={twJoin(
                            'fixed inset-y-0 left-0 right-1 z-30 overflow-y-auto',
                            'md:absolute md:inset-y-auto md:left-auto md:right-0 md:top-12 md:z-10 md:max-h-[50vh] md:min-w-popup md:max-w-prose',
                            'bg-white md:rounded-lg md:shadow-lg',
                            'animate-in fade-in slide-in-from-left-full md:slide-in-from-left-0 md:slide-in-from-top-10',
                        )}
                        tabIndex={-1}
                        onKeyDown={handleKeyDown}
                        role="menu"
                        id="profile-menu">
                        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3 md:hidden">
                            <div className="inline-flex items-center gap-2">
                                <div className="rounded-full bg-neutral-100 p-2.5">
                                    <UserIcon className="size-5 text-neutral-500" />
                                </div>
                                <span>{profile?.shortName ?? t('auth.profile')}</span>
                            </div>

                            <button className="p-1" onClick={handleClose}>
                                <CloseIcon className="size-4" />
                            </button>
                        </div>

                        <div className="max-h-full select-none p-4">
                            {clients.length === 0 && !isEsales && (
                                <Message
                                    containerClassName="@sm:items-center"
                                    variant="warning"
                                    addon={
                                        <Button
                                            href={ROUTES.SELF_SERVICE.PROFILE.REGISTER}
                                            onClick={handleClose}
                                            size="small"
                                            rightAddon={<ArrowRightIcon className="size-4" />}>
                                            {t('profile.updateProfile')}
                                        </Button>
                                    }>
                                    {t('profile.confirmContactDetails')}
                                </Message>
                            )}

                            {clients.length !== 0 && (
                                <>
                                    <div className="mb-3 text-sm text-neutral-500">{t('auth.chooseClient')}</div>

                                    <ClientSwitcher
                                        clients={clients}
                                        selectedClientId={selectedClientId}
                                        onSuccess={handleClose}
                                    />
                                </>
                            )}

                            <hr className="my-4 border-neutral-300" />

                            <ul className="flex flex-col gap-1">
                                {!isEsales && (
                                    <ProfileMenuButton
                                        href={ROUTES.SELF_SERVICE.PROFILE.ROOT}
                                        icon={<UserIcon className="size-5" />}
                                        onClick={handleClose}
                                        aria-label={t('auth.myProfile')}>
                                        {t('auth.myProfile')}
                                    </ProfileMenuButton>
                                )}
                                {!isEsales && profile?.isCompensaLifeClient && (
                                    <ProfileMenuButton
                                        href={ROUTES.SELF_SERVICE.MESSAGES}
                                        icon={<EmailIcon className="size-5" />}
                                        onClick={handleClose}
                                        aria-label={t('auth.messages')}>
                                        {t('auth.messages')}
                                    </ProfileMenuButton>
                                )}

                                <ProfileMenuButton
                                    onClick={handleLogout}
                                    icon={<LogoutIcon className="size-5" />}
                                    aria-label={t('auth.logout')}>
                                    {t('auth.logout')}
                                </ProfileMenuButton>
                            </ul>

                            {!isEsales && <hr className="my-4 border-neutral-300 md:hidden" />}

                            {!isEsales && <AccessibilityMenu className="px-4 md:hidden" extended />}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
